'use strict';

const libPictProvider = require('pict-provider');
const libCertConfig = require('../Certification-Config.js');

// Mount points (the CertificationTypes view lays these out; this provider fills them with hand-built HTML).
const RAIL_MOUNT = '#CertTypes-Rail';
const RAIL_LIST_MOUNT = '#CertTypes-Rail-List';
const DETAIL_MOUNT = '#CertTypes-Detail';

const HOLDER_CHOICES = libCertConfig.HOLDER_CERTIFIABLE_LIST;   // User/Material/Lab/Equipment/Test
const APPLIES_CHOICES = libCertConfig.APPLIES_TO_KIND_LIST;     // Material/Lab/Test/Equipment
const STARTER_TYPES = libCertConfig.STARTER_TYPES;             // suggested starter catalog for empty tenants
const KIND_LABEL = (pKind) => (libCertConfig.HOLDER_KIND_BY_TYPE[pKind] ? libCertConfig.HOLDER_KIND_BY_TYPE[pKind].Label : pKind);

/**
 * CertificationTypes — the #/CertificationTypes management screen. Create/edit the certification-type CATALOG
 * (Name / Hash / Verb / Description) AND each type's per-type CONFIG (which entity kinds it certifies — holders — and
 * certifies-for, plus a default validity), stored in the cascading Configuration service (namespace
 * CertificationTypeConfig, one NamespaceValue per type). That config drives the "simplified interface" on the
 * #/Certifications workspace (pre-scoped / collapsed kind pickers).
 *
 * Gated by the CertificationType capabilities (Read to see, Update/Create/Delete to edit) — server stays
 * authoritative. Config writes go to Customer scope; the client never sends IDCustomer.
 */
class CertificationTypesProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._typeSearchTimer = null;
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	callerCustomer() { const tmpUser = this.pict.AppData.CurrentUser; return tmpUser ? Number(tmpUser.CustomerID) : 0; }

	// ── Capability gates (server authoritative; fail-open before the map loads) ───
	_can(pEntity, pAction)
	{
		const tmpApp = this.pict.fable;   // the EffectivePermissionsService lives on fable, not the app instance
		if (tmpApp && tmpApp.EffectivePermissionsService && (typeof tmpApp.EffectivePermissionsService.can === 'function')) { return tmpApp.EffectivePermissionsService.can(pEntity, pAction); }
		return true;
	}
	canCreate() { return this._can('CertificationType', 'Create'); }
	canEditDef() { return this._can('CertificationType', 'Update'); }
	canDelete() { return this._can('CertificationType', 'Delete'); }

	get state()
	{
		if (!this.pict.AppData.CertificationTypes)
		{
			this.pict.AppData.CertificationTypes =
			{
				Loaded: false, Loading: false,
				Types: [], TypeSearch: '',
				SelectedTypeID: 0, SelectedType: null,
				// definition editor
				Editing: { Name: '', Hash: '', Verb: '', Description: '' }, DefSaving: false, DefError: '', DefNotice: '',
				// per-type config editor (normalized blob or null while loading)
				Config: null, ConfigSaving: false, ConfigError: '', ConfigNotice: '',
				// create / delete
				ShowNew: false, NewFields: { Name: '', Hash: '', Verb: '', Description: '' }, NewSaving: false, NewError: '',
				DeleteConfirm: false, Deleting: false,
				// starter suggestions (shown while the catalog started empty)
				StarterMode: false, AdoptBusy: -1, StarterNotice: '',
			};
		}
		return this.pict.AppData.CertificationTypes;
	}

	// ── REST helpers ─────────────────────────────────────────────────────────────
	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_postJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.postJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_putJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.putJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_deleteEntity(pEntity, pID) { const tmpRC = this.restClient; return (tmpRC && (typeof tmpRC.deleteEntity === 'function')) ? new Promise((resolve) => tmpRC.deleteEntity(pEntity, pID, (pErr) => resolve(!pErr))) : Promise.resolve(false); }

	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
	_typeLabel(pType) { if (!pType) { return ''; } return String(pType.Name || pType.Hash || `Type ${pType.IDCertificationType}`); }
	_typeSub(pType) { if (!pType) { return ''; } return [ pType.Verb, (pType.Name ? pType.Hash : '') ].filter((pBit) => pBit).join(' · '); }

	// ── Load ─────────────────────────────────────────────────────────────────────
	loadAsync()
	{
		const tmpState = this.state;
		if (tmpState.Loading) { return; }
		tmpState.Loading = true;
		this.render();
		this._getJSON(`CertificationTypes/FilteredTo/FBV~Deleted~EQ~0~FSF~Hash~ASC~0/0/2000`).then((pRows) =>
		{
			tmpState.Types = (Array.isArray(pRows) ? pRows : []).slice();
			// An empty catalog gets the "get started" suggestions; stays on for this session so several can be adopted.
			if (!tmpState.Types.length) { tmpState.StarterMode = true; }
			tmpState.Loaded = true;
			tmpState.Loading = false;
			this.render();
		});
	}

	// ── Starter suggestions (empty-tenant onboarding) ────────────────────────────
	_unadoptedStarters()
	{
		const tmpState = this.state;
		const tmpHave = {};
		(tmpState.Types || []).forEach((pT) => { if (pT.Hash) { tmpHave[String(pT.Hash).toLowerCase()] = true; } if (pT.Name) { tmpHave[String(pT.Name).toLowerCase()] = true; } });
		return STARTER_TYPES.map((pStarter, pIndex) => ({ Starter: pStarter, Index: pIndex }))
			.filter((pRow) => !tmpHave[String(pRow.Starter.Hash).toLowerCase()] && !tmpHave[String(pRow.Starter.Name).toLowerCase()]);
	}

	/** Return to the suggestions panel (deselect any type). */
	showSuggestions() { const tmpState = this.state; tmpState.SelectedTypeID = 0; tmpState.SelectedType = null; tmpState.ShowNew = false; tmpState.StarterNotice = ''; this.render(); }

	/** Adopt a suggested type: create the CertificationType, then best-effort save its bundled config. Stays on the
	 * suggestions panel so several can be adopted in a row. */
	async adoptStarterAsync(pIndex)
	{
		const tmpState = this.state;
		if (!this.canCreate() || (tmpState.AdoptBusy > -1)) { return; }
		const tmpStarter = STARTER_TYPES[Number(pIndex)];
		if (!tmpStarter) { return; }
		tmpState.AdoptBusy = Number(pIndex); tmpState.StarterNotice = '';
		this.render();
		const tmpRes = await this._postJSON('CertificationType', { IDCertificationType: 0, Name: tmpStarter.Name, Hash: tmpStarter.Hash, Verb: tmpStarter.Verb, Description: tmpStarter.Description });
		const tmpType = tmpRes && tmpRes.result;
		if (tmpRes.error || !tmpType || !tmpType.IDCertificationType)
		{
			tmpState.AdoptBusy = -1; tmpState.StarterNotice = `Could not add “${tmpStarter.Name}” (you may lack permission).`;
			this.render();
			return;
		}
		tmpState.Types.push(tmpType);
		tmpState.Types.sort((pA, pB) => String(pA.Hash || pA.Name || '').localeCompare(String(pB.Hash || pB.Name || '')));
		// best-effort: apply the starter's bundled config so the adopted type is ready + simplified (Customer scope may 403)
		const tmpBlob = { HolderKinds: tmpStarter.HolderKinds.slice(), AppliesToKinds: tmpStarter.AppliesToKinds.slice(), DefaultValidityYears: Number(tmpStarter.DefaultValidityYears) || 0 };
		await this._postJSON(`Configuration/Put/${libCertConfig.CERT_CONFIG_NAMESPACE}/${libCertConfig.configNamespaceValue(tmpType.IDCertificationType)}/Customer/${this.callerCustomer()}`, { Config: tmpBlob });
		tmpState.AdoptBusy = -1; tmpState.StarterNotice = `Added “${tmpStarter.Name}”.`;
		this.render();
	}

	setTypeSearch(pValue)
	{
		this.state.TypeSearch = String(pValue == null ? '' : pValue);
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(RAIL_LIST_MOUNT).length > 0) { tmpCA.assignContent(RAIL_LIST_MOUNT, this._railListHTML()); }
		else { this.render(); }
	}

	selectType(pIDCertificationType)
	{
		const tmpState = this.state;
		const tmpID = Number(pIDCertificationType);
		tmpState.SelectedTypeID = tmpID;
		tmpState.SelectedType = (tmpState.Types || []).find((pType) => Number(pType.IDCertificationType) === tmpID) || null;
		tmpState.ShowNew = false;
		tmpState.DeleteConfirm = false;
		tmpState.DefError = ''; tmpState.DefNotice = ''; tmpState.ConfigError = ''; tmpState.ConfigNotice = '';
		tmpState.Config = null;
		const tmpType = tmpState.SelectedType;
		tmpState.Editing = tmpType
			? { Name: String(tmpType.Name || ''), Hash: String(tmpType.Hash || ''), Verb: String(tmpType.Verb || ''), Description: String(tmpType.Description || '') }
			: { Name: '', Hash: '', Verb: '', Description: '' };
		this.render();
		if (tmpID > 0) { this._loadConfigAsync(tmpID, tmpType); }
	}

	_loadConfigAsync(pTypeID, pType)
	{
		const tmpState = this.state;
		this._postJSON('Configuration/Get', { LevelContext: 'Customer', LevelContextID: this.callerCustomer(), NamespaceKey: libCertConfig.CERT_CONFIG_NAMESPACE, NamespaceValue: libCertConfig.configNamespaceValue(pTypeID) }).then((pRes) =>
		{
			if (Number(tmpState.SelectedTypeID) !== Number(pTypeID)) { return; }
			const tmpBody = (pRes && (pRes.result !== undefined)) ? pRes.result : pRes;
			const tmpRaw = (tmpBody && (tmpBody.Config !== undefined)) ? tmpBody.Config : ((tmpBody && tmpBody.Document) || tmpBody);
			tmpState.Config = libCertConfig.normalizeConfig(tmpRaw, pType);
			this.render();
		});
	}

	// ── Definition (Name / Hash / Verb / Description) ────────────────────────────
	setEditField(pField, pValue) { const tmpState = this.state; if (Object.prototype.hasOwnProperty.call(tmpState.Editing, pField)) { tmpState.Editing[pField] = String(pValue == null ? '' : pValue); } }

	async saveDefAsync()
	{
		const tmpState = this.state;
		if (!this.canEditDef() || tmpState.DefSaving) { return; }
		const tmpType = tmpState.SelectedType;
		if (!tmpType) { return; }
		const tmpName = String(tmpState.Editing.Name || '').trim();
		const tmpHash = String(tmpState.Editing.Hash || '').trim();
		if (!tmpName && !tmpHash) { tmpState.DefError = 'Give the certification a name (or a hash/code).'; tmpState.DefNotice = ''; this.render(); return; }
		tmpState.DefSaving = true; tmpState.DefError = ''; tmpState.DefNotice = '';
		this.render();
		const tmpBody = Object.assign({}, tmpType, { Name: tmpName, Hash: tmpHash, Verb: String(tmpState.Editing.Verb || '').trim(), Description: String(tmpState.Editing.Description || '') });
		const tmpRes = await this._putJSON(`CertificationType/${Number(tmpType.IDCertificationType)}`, tmpBody);
		tmpState.DefSaving = false;
		if (tmpRes.error) { tmpState.DefError = 'The server rejected the change (you may lack permission).'; this.render(); return; }
		const tmpSaved = (tmpRes.result && tmpRes.result.IDCertificationType) ? tmpRes.result : tmpBody;
		const tmpIdx = tmpState.Types.findIndex((pT) => Number(pT.IDCertificationType) === Number(tmpType.IDCertificationType));
		if (tmpIdx > -1) { tmpState.Types[tmpIdx] = tmpSaved; }
		tmpState.SelectedType = tmpSaved;
		tmpState.Types.sort((pA, pB) => String(pA.Hash || pA.Name || '').localeCompare(String(pB.Hash || pB.Name || '')));
		tmpState.DefNotice = 'Saved.';
		this.render();
	}

	// ── Per-type config (holders / certifies-for / validity) ─────────────────────
	toggleConfigKind(pList, pKind, pChecked)
	{
		const tmpState = this.state;
		if (!tmpState.Config) { return; }
		const tmpKey = (pList === 'Holder') ? 'HolderKinds' : 'AppliesToKinds';
		const tmpAllowed = (pList === 'Holder') ? HOLDER_CHOICES : APPLIES_CHOICES;
		if (tmpAllowed.indexOf(pKind) === -1) { return; }
		const tmpSet = tmpState.Config[tmpKey].filter((pK) => pK !== pKind);
		if (pChecked) { tmpSet.push(pKind); }
		// preserve canonical order
		tmpState.Config[tmpKey] = tmpAllowed.filter((pK) => tmpSet.indexOf(pK) > -1);
		tmpState.ConfigNotice = ''; tmpState.ConfigError = '';
	}

	setConfigValidity(pValue)
	{
		const tmpState = this.state;
		if (!tmpState.Config) { return; }
		const tmpN = Number(pValue);
		tmpState.Config.DefaultValidityYears = (tmpN >= 0 && Number.isFinite(tmpN)) ? tmpN : 0;
		tmpState.ConfigNotice = ''; tmpState.ConfigError = '';
	}

	async saveConfigAsync()
	{
		const tmpState = this.state;
		if (!this.canEditDef() || tmpState.ConfigSaving || !tmpState.Config || !tmpState.SelectedType) { return; }
		if (!tmpState.Config.HolderKinds.length) { tmpState.ConfigError = 'Pick at least one kind this certification certifies.'; this.render(); return; }
		tmpState.ConfigSaving = true; tmpState.ConfigError = ''; tmpState.ConfigNotice = '';
		this.render();
		const tmpBlob = { HolderKinds: tmpState.Config.HolderKinds.slice(), AppliesToKinds: tmpState.Config.AppliesToKinds.slice(), DefaultValidityYears: Number(tmpState.Config.DefaultValidityYears) || 0 };
		const tmpNSV = libCertConfig.configNamespaceValue(tmpState.SelectedTypeID);
		const tmpRes = await this._postJSON(`Configuration/Put/${libCertConfig.CERT_CONFIG_NAMESPACE}/${tmpNSV}/Customer/${this.callerCustomer()}`, { Config: tmpBlob });
		tmpState.ConfigSaving = false;
		if (tmpRes.error) { tmpState.ConfigError = 'The server rejected the configuration (Customer-level config may need an administrator).'; this.render(); return; }
		tmpState.ConfigNotice = 'Configuration saved.';
		// re-read so the view reflects the server's cascade/diff
		this._loadConfigAsync(Number(tmpState.SelectedTypeID), tmpState.SelectedType);
	}

	// ── New type ─────────────────────────────────────────────────────────────────
	showNew() { const tmpState = this.state; tmpState.ShowNew = true; tmpState.SelectedTypeID = 0; tmpState.SelectedType = null; tmpState.NewFields = { Name: '', Hash: '', Verb: '', Description: '' }; tmpState.NewError = ''; this.render(); }
	setNewField(pField, pValue) { const tmpState = this.state; if (Object.prototype.hasOwnProperty.call(tmpState.NewFields, pField)) { tmpState.NewFields[pField] = String(pValue == null ? '' : pValue); } }
	cancelNew() { this.state.ShowNew = false; this.render(); }

	async createTypeAsync()
	{
		const tmpState = this.state;
		if (!this.canCreate() || tmpState.NewSaving) { return; }
		const tmpName = String(tmpState.NewFields.Name || '').trim();
		const tmpHash = String(tmpState.NewFields.Hash || '').trim();
		if (!tmpName && !tmpHash) { tmpState.NewError = 'Give the certification a name (or a hash/code).'; this.render(); return; }
		tmpState.NewSaving = true; tmpState.NewError = '';
		this.render();
		const tmpRes = await this._postJSON('CertificationType', { IDCertificationType: 0, Name: tmpName, Hash: tmpHash, Verb: String(tmpState.NewFields.Verb || '').trim(), Description: String(tmpState.NewFields.Description || '') });
		tmpState.NewSaving = false;
		const tmpType = tmpRes && tmpRes.result;
		if (tmpRes.error || !tmpType || !tmpType.IDCertificationType) { tmpState.NewError = 'The server rejected the new certification type.'; this.render(); return; }
		tmpState.Types.push(tmpType);
		tmpState.Types.sort((pA, pB) => String(pA.Hash || pA.Name || '').localeCompare(String(pB.Hash || pB.Name || '')));
		tmpState.ShowNew = false;
		this.selectType(Number(tmpType.IDCertificationType));
	}

	// ── Delete ───────────────────────────────────────────────────────────────────
	askDelete() { this.state.DeleteConfirm = true; this.render(); }
	cancelDelete() { this.state.DeleteConfirm = false; this.render(); }
	async deleteTypeAsync()
	{
		const tmpState = this.state;
		if (!this.canDelete() || tmpState.Deleting || !tmpState.SelectedType) { return; }
		tmpState.Deleting = true;
		this.render();
		const tmpID = Number(tmpState.SelectedTypeID);
		const tmpOK = await this._deleteEntity('CertificationType', tmpID);
		tmpState.Deleting = false;
		tmpState.DeleteConfirm = false;
		if (!tmpOK) { tmpState.DefError = 'The server refused to delete this certification type (you may lack permission, or it is in use).'; this.render(); return; }
		tmpState.Types = tmpState.Types.filter((pT) => Number(pT.IDCertificationType) !== tmpID);
		tmpState.SelectedTypeID = 0; tmpState.SelectedType = null; tmpState.Config = null;
		this.render();
	}

	/** Jump to the #/Certifications workspace to certify holders of the selected type (hash router). */
	goCertify() { window.location.hash = '#/Certifications'; }

	// ── Render ───────────────────────────────────────────────────────────────────
	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(RAIL_MOUNT).length === 0) { return; }
		if (tmpCA.getElement(RAIL_LIST_MOUNT).length === 0) { tmpCA.assignContent(RAIL_MOUNT, this._railHTML()); }
		else { tmpCA.assignContent(RAIL_LIST_MOUNT, this._railListHTML()); }
		tmpCA.assignContent(DETAIL_MOUNT, this._detailHTML());
	}

	_railHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Loaded) { return '<div class="ctp-empty">Loading…</div>'; }
		const tmpNew = this.canCreate() ? `<button class="ctp-newtype-btn" onclick="_Pict.providers.CertificationTypes.showNew()"><i class="fas fa-plus"></i> New certification type</button>` : '';
		// While bootstrapping an empty tenant, offer a way back to the suggestions once a type is selected / being created.
		const tmpSuggest = (tmpState.StarterMode && this._unadoptedStarters().length && (tmpState.SelectedTypeID || tmpState.ShowNew)) ? `<button class="ctp-newtype-btn ctp-suggest-btn" onclick="_Pict.providers.CertificationTypes.showSuggestions()"><i class="fas fa-wand-magic-sparkles"></i> Suggested types</button>` : '';
		return `<input class="ctp-rail-search" type="text" autocomplete="off" spellcheck="false" placeholder="Search certification types…" value="${this._esc(tmpState.TypeSearch || '')}" oninput="_Pict.providers.CertificationTypes.setTypeSearch(this.value)" />`
			+ tmpNew + tmpSuggest
			+ `<div id="CertTypes-Rail-List" class="ctp-rail-list">${this._railListHTML()}</div>`;
	}

	_railListHTML()
	{
		const tmpState = this.state;
		const tmpQuery = String(tmpState.TypeSearch || '').trim().toLowerCase();
		const tmpTypes = (tmpState.Types || []).filter((pType) => !tmpQuery
			|| String(this._typeLabel(pType)).toLowerCase().indexOf(tmpQuery) > -1
			|| String(pType.Verb || '').toLowerCase().indexOf(tmpQuery) > -1
			|| String(pType.Description || '').toLowerCase().indexOf(tmpQuery) > -1);
		if (!tmpTypes.length) { return `<div class="ctp-empty">${tmpQuery ? 'No certification types match.' : 'No certification types yet.'}</div>`; }
		return tmpTypes.map((pType) =>
		{
			const tmpActive = (Number(tmpState.SelectedTypeID) === Number(pType.IDCertificationType)) ? ' is-active' : '';
			const tmpSub = this._typeSub(pType);
			return `<div class="ctp-rail-item${tmpActive}" onclick="_Pict.providers.CertificationTypes.selectType(${Number(pType.IDCertificationType)})"><div class="ctp-rail-name">${this._esc(this._typeLabel(pType))}</div>${tmpSub ? `<div class="ctp-rail-sub">${this._esc(tmpSub)}</div>` : ''}</div>`;
		}).join('');
	}

	_detailHTML()
	{
		const tmpState = this.state;
		if (tmpState.ShowNew) { return this._newTypeHTML(); }
		if (!tmpState.SelectedTypeID || !tmpState.SelectedType)
		{
			if (tmpState.StarterMode) { return this._suggestionsHTML(); }
			return '<div class="ctp-detail-empty">Pick a certification type on the left to edit it and configure what it certifies — or create a new type.</div>';
		}
		const tmpType = tmpState.SelectedType;
		return `<div class="ctp-detail-head"><div class="ctp-detail-title">${this._esc(this._typeLabel(tmpType))}</div>`
			+ `<div class="ctp-detail-sub">${this._esc(this._typeSub(tmpType))}</div>`
			+ `<button class="ctp-btn ctp-btn-ghost ctp-go" onclick="_Pict.providers.CertificationTypes.goCertify()"><i class="fas fa-award"></i> Certify holders →</button></div>`
			+ this._defHTML()
			+ this._configHTML()
			+ this._dangerHTML();
	}

	_suggestionsHTML()
	{
		const tmpState = this.state;
		const tmpUnadopted = this._unadoptedStarters();
		const tmpNotice = tmpState.StarterNotice ? `<div class="ctp-notice">${this._esc(tmpState.StarterNotice)}</div>` : '';
		const tmpCanCreate = this.canCreate();
		if (!tmpUnadopted.length)
		{
			return `<div class="ctp-detail-head"><div class="ctp-detail-title">You're all set</div><div class="ctp-detail-sub">Every suggested certification type has been added.</div></div>`
				+ tmpNotice
				+ `<div class="ctp-section"><p class="ctp-note">Pick a certification type on the left to configure what it certifies — or add your own with <b>+ New certification type</b>.</p></div>`;
		}
		const tmpCards = tmpUnadopted.map((pRow) =>
		{
			const tmpStarter = pRow.Starter;
			const tmpHolders = tmpStarter.HolderKinds.map((pK) => KIND_LABEL(pK)).join(', ');
			const tmpApplies = tmpStarter.AppliesToKinds.map((pK) => KIND_LABEL(pK)).join(', ');
			const tmpValidity = (tmpStarter.DefaultValidityYears > 0) ? ` · ${tmpStarter.DefaultValidityYears}-yr validity` : '';
			const tmpBusy = (tmpState.AdoptBusy === pRow.Index);
			const tmpBtn = tmpCanCreate ? `<button class="ctp-btn ctp-btn-primary ctp-adopt"${tmpBusy ? ' disabled' : ''} onclick="_Pict.providers.CertificationTypes.adoptStarterAsync(${pRow.Index})">${tmpBusy ? 'Adding…' : 'Adopt'}</button>` : '';
			return `<div class="ctp-suggest"><div class="ctp-suggest-main">`
				+ `<div class="ctp-suggest-name">${this._esc(tmpStarter.Name)}</div>`
				+ `<div class="ctp-suggest-desc">${this._esc(tmpStarter.Description)}</div>`
				+ `<div class="ctp-suggest-meta">Certifies ${this._esc(tmpHolders)} · for ${this._esc(tmpApplies)}${this._esc(tmpValidity)}</div>`
				+ `</div>${tmpBtn}</div>`;
		}).join('');
		return `<div class="ctp-detail-head"><div class="ctp-detail-title">Get started</div><div class="ctp-detail-sub">No certification types yet — adopt a common one to create it (pre-configured), then tweak as needed.</div></div>`
			+ tmpNotice
			+ `<div class="ctp-suggest-list">${tmpCards}</div>`
			+ (tmpCanCreate ? `<p class="ctp-note">…or use <b>+ New certification type</b> on the left to build your own.</p>` : '');
	}

	_defHTML()
	{
		const tmpState = this.state;
		const tmpEditable = this.canEditDef();
		const tmpE = tmpState.Editing;
		const tmpDis = tmpEditable ? '' : ' disabled';
		const tmpNotice = tmpState.DefNotice ? `<div class="ctp-notice">${this._esc(tmpState.DefNotice)}</div>` : '';
		const tmpError = tmpState.DefError ? `<div class="ctp-error">${this._esc(tmpState.DefError)}</div>` : '';
		const tmpBusy = tmpState.DefSaving ? '<span class="ctp-busy">saving…</span>' : '';
		const tmpSave = tmpEditable ? `<div class="ctp-add-row"><button class="ctp-btn ctp-btn-primary" onclick="_Pict.providers.CertificationTypes.saveDefAsync()">Save definition</button>${tmpBusy}</div>` : '<p class="ctp-note">Editing certification types needs the Update-certification-type permission.</p>';
		return `<div class="ctp-section"><div class="ctp-section-head">Definition</div>`
			+ `<label class="ctp-field"><span>Name</span><input type="text"${tmpDis} value="${this._esc(tmpE.Name)}" oninput="_Pict.providers.CertificationTypes.setEditField('Name', this.value)"></label>`
			+ `<label class="ctp-field"><span>Verb</span><input type="text"${tmpDis} value="${this._esc(tmpE.Verb)}" placeholder="e.g. Collect Sample" oninput="_Pict.providers.CertificationTypes.setEditField('Verb', this.value)"></label>`
			+ `<label class="ctp-field"><span>Hash / code</span><input type="text"${tmpDis} value="${this._esc(tmpE.Hash)}" placeholder="(short code)" oninput="_Pict.providers.CertificationTypes.setEditField('Hash', this.value)"></label>`
			+ `<label class="ctp-field"><span>Description</span><textarea rows="2"${tmpDis} oninput="_Pict.providers.CertificationTypes.setEditField('Description', this.value)">${this._esc(tmpE.Description)}</textarea></label>`
			+ tmpSave + tmpNotice + tmpError + `</div>`;
	}

	_kindCheckboxes(pList, pChoices, pSelected)
	{
		const tmpEditable = this.canEditDef();
		return pChoices.map((pKind) =>
		{
			const tmpChecked = (pSelected.indexOf(pKind) > -1) ? ' checked' : '';
			const tmpDis = tmpEditable ? '' : ' disabled';
			return `<label class="ctp-check"><input type="checkbox"${tmpChecked}${tmpDis} onchange="_Pict.providers.CertificationTypes.toggleConfigKind('${pList}', '${pKind}', this.checked)"> ${this._esc(KIND_LABEL(pKind))}</label>`;
		}).join('');
	}

	_configHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Config) { return `<div class="ctp-section"><div class="ctp-section-head">What this certifies</div><p class="ctp-note">Loading…</p></div>`; }
		const tmpCfg = tmpState.Config;
		const tmpEditable = this.canEditDef();
		const tmpDefaultsNote = tmpCfg.Configured ? '' : `<div class="ctp-hint"><i class="fas fa-circle-info"></i> Using built-in defaults — not yet saved for this type. Adjust and save to lock them in.</div>`;
		const tmpNotice = tmpState.ConfigNotice ? `<div class="ctp-notice">${this._esc(tmpState.ConfigNotice)}</div>` : '';
		const tmpError = tmpState.ConfigError ? `<div class="ctp-error">${this._esc(tmpState.ConfigError)}</div>` : '';
		const tmpBusy = tmpState.ConfigSaving ? '<span class="ctp-busy">saving…</span>' : '';
		const tmpValidity = `<label class="ctp-field ctp-field-inline"><span>Default validity (years)</span><input type="number" min="0" step="1"${tmpEditable ? '' : ' disabled'} value="${Number(tmpCfg.DefaultValidityYears) || 0}" onchange="_Pict.providers.CertificationTypes.setConfigValidity(this.value)"><span class="ctp-hint-inline">0 = no default expiry</span></label>`;
		const tmpSave = tmpEditable ? `<div class="ctp-add-row"><button class="ctp-btn ctp-btn-primary" onclick="_Pict.providers.CertificationTypes.saveConfigAsync()">Save configuration</button>${tmpBusy}</div>` : '';
		return `<div class="ctp-section"><div class="ctp-section-head">What this certifies</div>`
			+ `<p class="ctp-note">Controls the #/Certifications screen: a type with a single holder kind hides the picker (a User-only cert just says “certify users”).</p>`
			+ tmpDefaultsNote
			+ `<div class="ctp-cfg-group"><div class="ctp-cfg-head">Certifies (holders)</div><div class="ctp-checks">${this._kindCheckboxes('Holder', HOLDER_CHOICES, tmpCfg.HolderKinds)}</div></div>`
			+ `<div class="ctp-cfg-group"><div class="ctp-cfg-head">Certifies for</div><div class="ctp-checks">${this._kindCheckboxes('Applies', APPLIES_CHOICES, tmpCfg.AppliesToKinds)}</div></div>`
			+ tmpValidity
			+ tmpSave + tmpNotice + tmpError + `</div>`;
	}

	_dangerHTML()
	{
		const tmpState = this.state;
		if (!this.canDelete()) { return ''; }
		if (tmpState.DeleteConfirm)
		{
			const tmpBusy = tmpState.Deleting ? '<span class="ctp-busy">deleting…</span>' : '';
			return `<div class="ctp-section ctp-danger"><div class="ctp-section-head">Delete</div>`
				+ `<p class="ctp-note">Delete “${this._esc(this._typeLabel(tmpState.SelectedType))}”? Existing certifications that reference it are not removed.</p>`
				+ `<div class="ctp-add-row"><button class="ctp-btn ctp-btn-danger" onclick="_Pict.providers.CertificationTypes.deleteTypeAsync()">Delete certification type</button><button class="ctp-btn ctp-btn-ghost" onclick="_Pict.providers.CertificationTypes.cancelDelete()">Cancel</button>${tmpBusy}</div></div>`;
		}
		return `<div class="ctp-section ctp-danger"><button class="ctp-btn ctp-btn-ghost ctp-btn-danger-link" onclick="_Pict.providers.CertificationTypes.askDelete()"><i class="fas fa-trash-can"></i> Delete this type</button></div>`;
	}

	_newTypeHTML()
	{
		const tmpState = this.state;
		const tmpF = tmpState.NewFields;
		const tmpError = tmpState.NewError ? `<div class="ctp-error">${this._esc(tmpState.NewError)}</div>` : '';
		const tmpBusy = tmpState.NewSaving ? '<span class="ctp-busy">saving…</span>' : '';
		return `<div class="ctp-detail-head"><div class="ctp-detail-title">New certification type</div><div class="ctp-detail-sub">Create the type, then configure what it certifies.</div></div>`
			+ `<div class="ctp-section">`
			+ `<label class="ctp-field"><span>Name</span><input type="text" value="${this._esc(tmpF.Name)}" placeholder="e.g. Asphalt Sampling" oninput="_Pict.providers.CertificationTypes.setNewField('Name', this.value)"></label>`
			+ `<label class="ctp-field"><span>Verb</span><input type="text" value="${this._esc(tmpF.Verb)}" placeholder="e.g. Collect Sample" oninput="_Pict.providers.CertificationTypes.setNewField('Verb', this.value)"></label>`
			+ `<label class="ctp-field"><span>Hash / code</span><input type="text" value="${this._esc(tmpF.Hash)}" placeholder="(optional short code)" oninput="_Pict.providers.CertificationTypes.setNewField('Hash', this.value)"></label>`
			+ `<label class="ctp-field"><span>Description</span><textarea rows="2" oninput="_Pict.providers.CertificationTypes.setNewField('Description', this.value)">${this._esc(tmpF.Description)}</textarea></label>`
			+ `<div class="ctp-add-row"><button class="ctp-btn ctp-btn-primary" onclick="_Pict.providers.CertificationTypes.createTypeAsync()">Create type</button><button class="ctp-btn ctp-btn-ghost" onclick="_Pict.providers.CertificationTypes.cancelNew()">Cancel</button>${tmpBusy}</div>`
			+ tmpError + `</div>`;
	}
}

module.exports = CertificationTypesProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'CertificationTypes',
	AutoInitialize: true,
	AutoInitializeOrdinal: 41,
};
